import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from "firebase/messaging";
import { apiFetch } from "../api/client";
import { esNativo } from "../lib/nativo";
import {
  activarNativo,
  apagarNativo,
  engancharNativo,
  estadoNativo,
  retomarNativo,
} from "./pushNativo";

/**
 * Web Push de la PWA (FCM). El mismo caño que la app Android: el token del
 * navegador se registra en POST /me/device-tokens con platform="web" y el
 * backend le manda por FCM HTTP v1 los mismos avisos.
 *
 * En iPhone funciona SOLO con la PWA instalada (Agregar a inicio, iOS 16.4+);
 * en Safari suelto `isSupported()` da false y los botones deben esconderse.
 *
 * Config pública (identificadores, no credenciales) — mismo criterio que el
 * DSN de Sentry. La clave VAPID es la PÚBLICA del par generado en Firebase.
 */

const firebaseConfig = {
  apiKey: "AIzaSyAIIlYDJkWi3JmksKvihUrmUqTgmEgMdBA",
  authDomain: "forja-ligas.firebaseapp.com",
  projectId: "forja-ligas",
  storageBucket: "forja-ligas.firebasestorage.app",
  messagingSenderId: "543904240458",
  appId: "1:543904240458:web:3a10ea595deb8be0baa7b3",
};

const VAPID_PUBLIC_KEY =
  "BKwTuubfJJmdWDL2cTUmgH6AEjtYlP6P0VktaiAVoawy4lXld6uPZhP1mzyUl5022j8eel94D52k2GhTRVdZSHM";

const LAST_TOKEN_KEY = "forja.push_token";

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let foregroundHooked = false;

async function getMessagingIfSupported(): Promise<Messaging | null> {
  if (!(await isSupported().catch(() => false))) return null;
  if (!messaging) {
    app = app ?? initializeApp(firebaseConfig);
    messaging = getMessaging(app);
  }
  return messaging;
}

/**
 * ¿Este navegador puede recibir push? (iOS: solo la PWA instalada.)
 *
 * ADENTRO DE LA APP la respuesta la da iOS, no el navegador: `isSupported()`
 * de Firebase Web mira el service worker, que en el contenedor no existe, y
 * diría que no se puede justo donde mejor funciona. Las cinco pantallas que
 * usan este módulo no tienen que enterarse de la diferencia, así que la
 * bifurcación vive acá y no en cada una de ellas (ver `src/lib/nativo.ts`).
 */
export async function pushSupported(): Promise<boolean> {
  if (esNativo()) return true;
  return (await isSupported().catch(() => false)) && "Notification" in window;
}

export type PushStatus = "enabled" | "blocked" | "off" | "unsupported";

/** Estado actual, para pintar el toggle sin pedir permisos. */
export async function pushStatus(): Promise<PushStatus> {
  if (esNativo()) return estadoNativo();
  if (!(await pushSupported())) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission === "granted" && localStorage.getItem(LAST_TOKEN_KEY)) {
    return "enabled";
  }
  return "off";
}

/**
 * Activa las notificaciones en ESTE dispositivo: pide permiso (llamar desde un
 * gesto del usuario — iOS lo exige), obtiene el token FCM y lo registra en el
 * backend. Idempotente.
 */
export async function enablePush(): Promise<PushStatus> {
  if (esNativo()) return activarNativo();
  const m = await getMessagingIfSupported();
  if (!m) return "unsupported";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "blocked" : "off";

  const token = await getToken(m, { vapidKey: VAPID_PUBLIC_KEY });
  if (!token) return "off";

  // `granted: true` no es decorativo (123): en el navegador el token NO existe
  // sin permiso, así que acá siempre es true — y eso es justamente lo que le
  // dice al backend que este dispositivo SÍ es alcanzable, a diferencia de
  // Android, donde el token se registra igual con el permiso apagado.
  await apiFetch("/api/v1/me/device-tokens", {
    method: "POST",
    body: { token, platform: "web", granted: true },
  });
  localStorage.setItem(LAST_TOKEN_KEY, token);
  hookForegroundMessages();
  return "enabled";
}

/** Apaga las notificaciones de este dispositivo (baja en backend + Firebase). */
export async function disablePush(): Promise<void> {
  if (esNativo()) return apagarNativo();
  const token = localStorage.getItem(LAST_TOKEN_KEY);
  localStorage.removeItem(LAST_TOKEN_KEY);
  if (token) {
    await apiFetch("/api/v1/me/device-tokens/remove", {
      method: "POST",
      body: { token },
    }).catch(() => undefined);
  }
  const m = await getMessagingIfSupported();
  if (m) await deleteToken(m).catch(() => undefined);
}

/**
 * Con la PWA ABIERTA las push no se muestran solas (eso es solo en background):
 * las mostramos a mano vía el service worker para que el aviso no se pierda.
 */
export function hookForegroundMessages(): void {
  if (esNativo()) {
    engancharNativo();
    return;
  }
  if (foregroundHooked) return;
  foregroundHooked = true;
  void getMessagingIfSupported().then((m) => {
    if (!m) return;
    onMessage(m, (payload) => {
      const title = payload.notification?.title;
      if (!title) return;
      void navigator.serviceWorker.ready.then((registration) =>
        registration.showNotification(title, {
          body: payload.notification?.body ?? "",
          icon: "/icons/icon-192.png",
          data: payload.fcmOptions?.link ? { link: payload.fcmOptions.link } : undefined,
        }),
      );
    });
  });
}

/**
 * Al abrir la app con push ya activado: re-registra el token (rota) y engancha
 * los avisos en primer plano. Silencioso, sin prompts.
 */
export async function resumePushIfEnabled(): Promise<void> {
  if (esNativo()) return retomarNativo();
  if (!(await pushSupported())) return;
  if (Notification.permission !== "granted") return;
  if (!localStorage.getItem(LAST_TOKEN_KEY)) return;
  try {
    const m = await getMessagingIfSupported();
    if (!m) return;
    const token = await getToken(m, { vapidKey: VAPID_PUBLIC_KEY });
    if (token && token !== localStorage.getItem(LAST_TOKEN_KEY)) {
      await apiFetch("/api/v1/me/device-tokens", {
        method: "POST",
        body: { token, platform: "web", granted: true },
      });
      localStorage.setItem(LAST_TOKEN_KEY, token);
    }
    hookForegroundMessages();
  } catch {
    // Best-effort: sin red o sin sesión, el próximo enable lo arregla.
  }
}
